const ROOT_SELECTOR = '[data-solena-space-preview]';
const MODEL_TIMEOUT = 16000;

const normalizeModelUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return '';

  const value = url.trim();
  if (value.startsWith('//')) return `https:${value}`;

  try {
    const normalized = new URL(value, window.location.origin);
    if (normalized.protocol === 'http:') normalized.protocol = 'https:';
    return normalized.href;
  } catch (error) {
    console.error('Solena model URL could not be normalized.', error);
    return '';
  }
};

const isValidGlbUrl = (url) => /\.glb(?:[?#]|$)/i.test(normalizeModelUrl(url));
const isValidUsdzUrl = (url) => /\.usdz(?:[?#]|$)/i.test(normalizeModelUrl(url));
const isAppleMobileDevice = () =>
  /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const getTemplate = (root, attribute, handle) =>
  Array.from(root.querySelectorAll(`template[${attribute}]`)).find(
    (template) => template.getAttribute(attribute) === handle
  );

const getJson = (root, attribute, handle) => {
  const element = Array.from(root.querySelectorAll(`script[${attribute}]`)).find(
    (script) => script.getAttribute(attribute) === handle
  );

  if (!element) return null;

  try {
    return JSON.parse(element.textContent || '{}');
  } catch (error) {
    console.error('Solena space preview data could not be read.', error);
    return null;
  }
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  input.remove();
};

const collectXrModels = (root) => {
  const models = [];

  root.querySelectorAll('[data-solena-space-xr-models]').forEach((element) => {
    try {
      const parsed = JSON.parse(element.textContent || '[]');
      if (Array.isArray(parsed)) models.push(...parsed);
    } catch (error) {
      console.error('Solena XR model data could not be read.', error);
    }
  });

  return models;
};

const addXrModels = (models) => {
  if (!window.ShopifyXR || !Array.isArray(models)) return;

  window.__solenaSpacePreviewModelIds ||= new Set();
  const freshModels = models.filter((model) => {
    const id = String(model?.id || '');
    if (!id || window.__solenaSpacePreviewModelIds.has(id)) return false;
    window.__solenaSpacePreviewModelIds.add(id);
    return true;
  });

  if (freshModels.length) window.ShopifyXR.addModels(freshModels);
  window.ShopifyXR.setupXRElements();
};

const setupModelViewerFeature = () => {
  if (customElements.get('model-viewer')) return Promise.resolve(true);
  if (!window.Shopify?.loadFeatures) return Promise.resolve(false);

  window.__solenaSpacePreviewModelViewerPromise ||= new Promise((resolve) => {
    let settled = false;

    const finish = (success) => {
      if (settled) return;
      settled = true;
      resolve(success);
    };

    customElements.whenDefined('model-viewer').then(() => finish(true));

    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad(errors) {
          if (errors) finish(false);
        },
      },
    ]);

    window.setTimeout(() => finish(Boolean(customElements.get('model-viewer'))), 10000);
  });

  return window.__solenaSpacePreviewModelViewerPromise;
};

const setupShopifyXr = (models) => {
  if (!window.Shopify?.loadFeatures) return Promise.resolve(false);

  if (window.ShopifyXR) {
    addXrModels(models);
    return Promise.resolve(true);
  }

  window.__solenaSpacePreviewXrPromise ||= new Promise((resolve) => {
    let settled = false;

    const finish = (success) => {
      if (settled) return;
      settled = true;
      resolve(success);
    };

    const initialize = () => {
      if (!window.ShopifyXR) return false;
      finish(true);
      return true;
    };

    document.addEventListener('shopify_xr_initialized', initialize, { once: true });

    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad(errors) {
          if (errors) {
            finish(false);
            return;
          }

          if (!initialize()) {
            document.addEventListener('shopify_xr_initialized', initialize, { once: true });
          }
        },
      },
    ]);

    window.setTimeout(() => finish(Boolean(window.ShopifyXR)), 10000);
  });

  return window.__solenaSpacePreviewXrPromise.then((ready) => {
    if (ready) addXrModels(models);
    return ready;
  });
};

const initSpacePreview = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.initialized === 'true') return;

  const productButtons = Array.from(root.querySelectorAll('[data-solena-space-product]'));
  const viewerFrame = root.querySelector('[data-solena-space-viewer-frame]');
  const viewerHost = root.querySelector('[data-solena-space-viewer-host]');
  const detailsHost = root.querySelector('[data-solena-space-details]');
  const loading = root.querySelector('[data-solena-space-loading]');
  const errorPanel = root.querySelector('[data-solena-space-error]');
  const unavailablePanel = root.querySelector('[data-solena-space-unavailable]');
  const tools = root.querySelector('[data-solena-space-tools]');
  const retryButton = root.querySelector('[data-solena-space-retry]');
  const rotateButton = root.querySelector('[data-solena-space-rotate]');
  const resetButton = root.querySelector('[data-solena-space-reset]');
  const fullscreenButton = root.querySelector('[data-solena-space-fullscreen]');
  const startButton = root.querySelector('[data-solena-space-start]');
  const gate = root.querySelector('[data-solena-space-gate]');

  if (!productButtons.length || !viewerFrame || !viewerHost || !detailsHost) return;

  root.dataset.initialized = 'true';

  const xrModels = collectXrModels(root);
  const productHandles = new Set(productButtons.map((button) => button.dataset.productHandle).filter(Boolean));
  const state = {
    handle: '',
    product: null,
    viewer: null,
    viewerAbort: null,
    loadTimer: 0,
    nativeSrc: '',
    nativeIosSrc: '',
    variantId: '',
    variantUnavailable: false,
    arMode: 'unavailable',
    arOptions: {},
    invalidHandles: new Set(),
    qrModulePromise: null,
  };

  const lazyActivation = root.dataset.lazyActivation === 'true';

  const setViewerState = (mode) => {
    if (loading) loading.hidden = mode !== 'loading';
    if (errorPanel) errorPanel.hidden = mode !== 'error';
    if (unavailablePanel) unavailablePanel.hidden = mode !== 'unavailable';
    if (tools) tools.hidden = mode !== 'ready';

    if (state.viewer) {
      state.viewer.classList.toggle('is-loaded', mode === 'ready');
      state.viewer.classList.toggle('is-unavailable', mode === 'unavailable');
    }
  };

  const clearLoadTimer = () => {
    window.clearTimeout(state.loadTimer);
    state.loadTimer = 0;
  };

  const beginLoading = () => {
    clearLoadTimer();
    state.variantUnavailable = false;
    root.dataset.state = 'loading';
    setViewerState('loading');

    state.loadTimer = window.setTimeout(() => {
      if (!state.variantUnavailable) failLoading();
    }, MODEL_TIMEOUT);
  };

  const finishLoading = () => {
    if (state.variantUnavailable) return;
    clearLoadTimer();
    root.dataset.state = 'ready';
    setViewerState('ready');
    updateArButtons(state.arMode, state.arOptions);
  };

  const failLoading = () => {
    if (state.variantUnavailable) return;
    clearLoadTimer();
    state.invalidHandles.add(state.handle);
    const failedButton = productButtons.find((button) => button.dataset.productHandle === state.handle);
    if (failedButton) failedButton.hidden = true;
    root.dataset.state = 'error';
    setViewerState('error');
  };

  const cleanupViewer = () => {
    clearLoadTimer();
    state.viewerAbort?.abort();
    state.viewerAbort = null;

    if (state.viewer) {
      try {
        state.viewer.pause?.();
        state.viewer.removeAttribute('src');
        state.viewer.removeAttribute('ios-src');
      } catch (error) {
        console.debug('Solena 3D viewer cleanup skipped.', error);
      }
    }

    state.viewer = null;
    state.nativeSrc = '';
    state.nativeIosSrc = '';
  };

  const previewUrl = (handle, variantId = '') => {
    const url = new URL(root.dataset.previewPath || window.location.pathname, window.location.origin);
    url.searchParams.set('product', handle);
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  };

  const updateBrowserUrl = (handle, variantId = '') => {
    const url = new URL(previewUrl(handle, variantId));
    window.history.replaceState({ solenaSpacePreview: true }, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const renderQrCode = async (url) => {
    const qrHost = detailsHost.querySelector('[data-solena-space-qr]');
    if (!qrHost || !root.dataset.qrModule) return;

    qrHost.replaceChildren();

    try {
      state.qrModulePromise ||= import(root.dataset.qrModule);
      const { QRCode } = await state.qrModulePromise;
      if (!qrHost.isConnected || state.handle !== new URL(url).searchParams.get('product')) return;

      new QRCode(qrHost, {
        text: url,
        width: 180,
        height: 180,
        alt: `Open ${state.product?.title || 'product'} AR preview on a phone`,
        colorDark: '#2F2118',
        colorLight: '#FFFFFF',
        useSVG: true,
      });
    } catch (error) {
      console.error('Solena preview QR code could not be generated.', error);
      qrHost.hidden = true;
    }
  };

  const updateShareActions = () => {
    const url = previewUrl(state.handle, state.variantId);
    const copyButton = detailsHost.querySelector('[data-solena-space-copy]');
    const productLink = detailsHost.querySelector('[data-solena-space-product-link]');

    if (copyButton) {
      copyButton.onclick = async () => {
        const originalLabel = copyButton.textContent;
        try {
          await copyText(url);
          copyButton.textContent = 'Link Copied';
        } catch (error) {
          console.error('Solena preview link could not be copied.', error);
          copyButton.textContent = 'Copy Failed';
        }

        window.setTimeout(() => {
          if (copyButton.isConnected) copyButton.textContent = originalLabel;
        }, 1800);
      };
    }

    if (productLink && state.product?.productUrl) {
      const productUrl = new URL(state.product.productUrl, window.location.origin);
      if (state.variantId) productUrl.searchParams.set('variant', state.variantId);
      productLink.href = `${productUrl.pathname}${productUrl.search}`;
    }

    renderQrCode(url);
  };

  const showArStatus = (message) => {
    const status = detailsHost.querySelector('[data-solena-space-ar-status]');
    if (status) status.textContent = message;
  };

  const updateArButtons = (mode, options = {}) => {
    const nativeButton = detailsHost.querySelector('[data-solena-space-native-ar]');
    const variantButton = detailsHost.querySelector('[data-solena-space-variant-ar]');
    const message = detailsHost.querySelector('[data-solena-space-variant-message]');
    const hasIosSource = isValidUsdzUrl(options.iosSrc);
    const iphoneUnavailable = mode === 'file' && isAppleMobileDevice() && !hasIosSource;
    const hasVerifiedArSource = mode === 'native' || (mode === 'file' && hasIosSource);
    const canActivateAr =
      hasVerifiedArSource &&
      !iphoneUnavailable &&
      state.viewer?.canActivateAR === true;

    state.arMode = mode;
    state.arOptions = options;
    if (nativeButton) nativeButton.hidden = true;
    if (variantButton) variantButton.hidden = !canActivateAr;
    if (message) message.hidden = mode !== 'unavailable';
    showArStatus(
      iphoneUnavailable ? 'AR preview is not supported on this device.' : ''
    );

    if (variantButton && canActivateAr) {
      variantButton.onclick = async () => {
        if (!state.viewer || typeof state.viewer.activateAR !== 'function') {
          showArStatus('AR is not supported on this device. You can continue using the 3D view.');
          return;
        }

        try {
          showArStatus('Opening the room preview...');
          if (state.arMode === 'native') await setupShopifyXr(xrModels);
          await state.viewer.activateAR();
          showArStatus('');
        } catch (error) {
          console.error('Solena variant AR could not be launched.', error);
          showArStatus('AR preview is not supported on this device.');
        }
      };
    }
  };

  const setViewerSource = (src, iosSrc = '') => {
    const normalizedSrc = normalizeModelUrl(src);
    const normalizedIosSrc = normalizeModelUrl(iosSrc);
    if (!state.viewer || !isValidGlbUrl(normalizedSrc)) return false;

    beginLoading();
    state.viewer.setAttribute('ar', '');
    state.viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
    state.viewer.setAttribute('src', normalizedSrc);

    if (isValidUsdzUrl(normalizedIosSrc)) state.viewer.setAttribute('ios-src', normalizedIosSrc);
    else state.viewer.removeAttribute('ios-src');

    if (state.viewer.loaded) finishLoading();
    return true;
  };

  const applyVariant = (variantId, options = {}) => {
    if (!state.product || !state.viewer) return;

    const variantKey = String(variantId || state.product.defaultVariantId || '');
    const variant = state.product.variants?.[variantKey];
    state.variantId = variantKey;

    const price = detailsHost.querySelector('[data-solena-space-price]');
    if (price && variant?.price) price.textContent = variant.price;

    const selector = detailsHost.querySelector('[data-solena-space-variant]');
    if (selector && Array.from(selector.options).some((option) => option.value === variantKey)) {
      selector.value = variantKey;
    }

    const variantGlbUrl = normalizeModelUrl(variant?.model_url);
    const variantUsdzUrl = normalizeModelUrl(variant?.usdz_url);
    const nativeGlbUrl = normalizeModelUrl(state.product.nativeGlbUrl || state.nativeSrc);
    const nativeUsdzUrl = normalizeModelUrl(state.product.nativeUsdzUrl || state.nativeIosSrc);

    if (isValidGlbUrl(variantGlbUrl)) {
      state.variantUnavailable = false;
      if (
        state.viewer.getAttribute('src') !== variantGlbUrl ||
        state.viewer.getAttribute('ios-src') !== (isValidUsdzUrl(variantUsdzUrl) ? variantUsdzUrl : null)
      ) {
        setViewerSource(variantGlbUrl, variantUsdzUrl);
      } else if (state.viewer.loaded) {
        finishLoading();
      }
      updateArButtons('file', { iosSrc: variantUsdzUrl });
    } else if (!state.product.hasVariantModels && isValidGlbUrl(nativeGlbUrl)) {
      state.variantUnavailable = false;
      if (
        state.viewer.getAttribute('src') !== nativeGlbUrl ||
        state.viewer.getAttribute('ios-src') !== (isValidUsdzUrl(nativeUsdzUrl) ? nativeUsdzUrl : null)
      ) {
        setViewerSource(nativeGlbUrl, nativeUsdzUrl);
      } else if (state.viewer.loaded) {
        finishLoading();
      }
      updateArButtons('native', { iosSrc: nativeUsdzUrl });
    } else {
      state.variantUnavailable = true;
      clearLoadTimer();
      state.viewer.removeAttribute('src');
      state.viewer.removeAttribute('ios-src');
      setViewerState('error');
      updateArButtons('error');
    }

    updateShareActions();
    if (options.updateUrl !== false) updateBrowserUrl(state.handle, state.variantId);
  };

  const setupViewer = () => {
    const viewer = viewerHost.querySelector('model-viewer');
    if (!viewer) {
      setViewerState('error');
      return;
    }

    state.viewer = viewer;
    state.nativeSrc = normalizeModelUrl(viewer.getAttribute('src'));
    state.nativeIosSrc = normalizeModelUrl(viewer.getAttribute('ios-src'));
    state.viewerAbort = new AbortController();
    const { signal } = state.viewerAbort;

    viewer.addEventListener('load', finishLoading, { signal });
    viewer.addEventListener('error', failLoading, { signal });
    viewer.addEventListener(
      'progress',
      (event) => {
        if (event.detail?.totalProgress >= 1) finishLoading();
      },
      { signal }
    );

    beginLoading();

    customElements.whenDefined('model-viewer').then(() => {
      if (state.viewer === viewer && viewer.loaded) finishLoading();
    });
  };

  const bindDetails = (requestedVariantId = '') => {
    const variantSelector = detailsHost.querySelector('[data-solena-space-variant]');
    const variantId =
      requestedVariantId && state.product?.variants?.[String(requestedVariantId)]
        ? String(requestedVariantId)
        : String(state.product?.defaultVariantId || '');

    if (variantSelector) {
      variantSelector.addEventListener('change', () => applyVariant(variantSelector.value));
    }

    applyVariant(variantId, { updateUrl: false });
  };

  const activateProduct = (handle, options = {}) => {
    if (!handle || !productHandles.has(handle)) return;

    const modelTemplate = getTemplate(root, 'data-solena-space-model-template', handle);
    const detailsTemplate = getTemplate(root, 'data-solena-space-details-template', handle);
    const productData = getJson(root, 'data-solena-space-product-data', handle);
    if (!modelTemplate || !detailsTemplate || !productData) return;

    cleanupViewer();
    state.handle = handle;
    state.product = productData;
    state.variantId = '';
    state.variantUnavailable = false;

    viewerHost.replaceChildren(modelTemplate.content.cloneNode(true));
    detailsHost.replaceChildren(detailsTemplate.content.cloneNode(true));

    productButtons.forEach((button) => {
      const active = button.dataset.productHandle === handle;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    setupViewer();
    bindDetails(options.variantId || '');
    updateShareActions();
    if (options.updateUrl !== false) updateBrowserUrl(handle, state.variantId);

    if (options.scrollCard) {
      productButtons
        .find((button) => button.dataset.productHandle === handle)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  productButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (lazyActivation && !state.viewer) {
        setupModelViewerFeature();
        gate?.setAttribute('hidden', '');
      }
      activateProduct(button.dataset.productHandle, { scrollCard: true });
    });
  });

  retryButton?.addEventListener('click', () => {
    if (!state.viewer || state.variantUnavailable) return;
    const src = state.viewer.getAttribute('src');
    const iosSrc = state.viewer.getAttribute('ios-src') || '';
    if (!src) {
      setViewerState('error');
      return;
    }

    state.viewer.removeAttribute('src');
    window.requestAnimationFrame(() => setViewerSource(src, iosSrc));
  });

  rotateButton?.addEventListener('click', () => {
    if (!state.viewer) return;
    const rotating = state.viewer.hasAttribute('auto-rotate');
    if (rotating) state.viewer.removeAttribute('auto-rotate');
    else state.viewer.setAttribute('auto-rotate', '');
    rotateButton.setAttribute('aria-pressed', String(!rotating));
    rotateButton.setAttribute('aria-label', rotating ? 'Start automatic rotation' : 'Pause automatic rotation');
  });

  resetButton?.addEventListener('click', () => {
    if (!state.viewer) return;
    state.viewer.removeAttribute('camera-orbit');
    state.viewer.removeAttribute('field-of-view');
    state.viewer.jumpCameraToGoal?.();
  });

  fullscreenButton?.addEventListener('click', async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await viewerFrame.requestFullscreen();
    } catch (error) {
      console.error('Solena 3D full screen could not be opened.', error);
    }
  });

  const initialParams = new URLSearchParams(window.location.search);
  const requestedHandle = initialParams.get('product');
  const requestedVariant = initialParams.get('variant');
  const initialHandle =
    requestedHandle && productHandles.has(requestedHandle) ? requestedHandle : root.dataset.firstProduct;

  const activateInitialProduct = () => {
    setupModelViewerFeature();
    gate?.setAttribute('hidden', '');
    activateProduct(initialHandle, {
      variantId: requestedHandle === initialHandle ? requestedVariant : '',
      updateUrl: false,
    });
  };

  if (lazyActivation) {
    if (loading) loading.hidden = true;
    startButton?.addEventListener('click', activateInitialProduct, { once: true });
  } else {
    activateInitialProduct();
  }

  root.__solenaSpacePreviewActivate = activateProduct;
};

const initializeAll = (scope = document) => {
  if (scope.matches?.(ROOT_SELECTOR)) initSpacePreview(scope);
  scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initSpacePreview);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAll());
} else {
  initializeAll();
}

document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

window.addEventListener('popstate', () => {
  const params = new URLSearchParams(window.location.search);
  const handle = params.get('product');
  const variantId = params.get('variant');

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    root.__solenaSpacePreviewActivate?.(handle || root.dataset.firstProduct, {
      variantId,
      updateUrl: false,
    });
  });
});
